// Features: M004-F0804
// Tests: T-M004-0448, T-M004-0449
// Owner-only fallback (until candidates ships): a 404 on candidates reads Core's existing GET /service-links/context and offers the
// room organisation's active Mycelium installations. 401 STEP_UP_REQUIRED opens the step-up dialog and retries once; 403 says so.
// WebUI #268: the Climate tab's "Linked Mycelium node" section. Shows the room's active service link, links one Mycelium
// candidate, and unlinks. Core decides rights (rooms_manage in the room's org): a 403 on the list hides the whole section.
// onLinksChanged tells the Climate tab to remount the controls panel, which re-reads describe (ROOM_LINK_AMBIGUOUS shows there).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import roomServiceLinksApi from '../../services/roomServiceLinksApi';
import StepUpDialog from '../auth/StepUpDialog';
import { getStepUpChallengeFromError } from '../../utils/stepUp';

const MYCELIUM = 'mycelium';
const OWNER_ONLY = 'Ask an organisation owner to link this room to its Mycelium node.';
const NO_NODE = 'No Mycelium node is available to link to this room yet.';
const LIST_FAILED = 'Nodes could not be listed right now.';
const LOAD_FAILED = 'The linked node could not be loaded right now.';
const AMBIGUOUS = 'This room is linked to more than one node; unlink the extra one.';
const PERMISSION = "You don't have permission to change this room's node link.";
const UNKNOWN = 'Something went wrong. Try again.';

// Keyed by the error code only. The server message is never shown.
export const LINK_REFUSAL_TEXT = Object.freeze({
  REQUEST_INVALID: 'That node could not be linked. Choose it again and retry.',
  INSTALLATION_NOT_FOUND: "That node is not available to this room's organisation.",
  INSTALLATION_NOT_ACTIVE: 'That node is not active right now, so it cannot be linked.',
  ROOM_LINK_EXISTS: 'This room is already linked to that node.',
  SERVICE_LINK_NOT_FOUND: 'That link no longer exists. The list has been refreshed.',
  SERVICE_LINK_ALREADY_REVOKED: 'That link was already removed. The list has been refreshed.',
  ORG_PERMISSION_DENIED: PERMISSION,
});

const statusOf = (cause) => Number(cause?.response?.status);
const codeOf = (cause) => {
  const error = cause?.response?.data?.error;
  if (typeof error === 'string') return error;
  return (error && typeof error.code === 'string' && error.code) || '';
};

export function linkRefusalText(cause) {
  const code = codeOf(cause);
  if (Object.prototype.hasOwnProperty.call(LINK_REFUSAL_TEXT, code)) return LINK_REFUSAL_TEXT[code];
  if (statusOf(cause) === 403) return PERMISSION;
  return UNKNOWN;
}

const shortId = (uuid) => `${String(uuid).slice(0, 8)}…`;

const dateText = (iso) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB', { timeZone: 'Europe/London' });
};

const RoomLinkedNodePanel = ({ roomId, organisationId, onLinksChanged }) => {
  // view: loading | ready | failed | hidden (hidden = 403 on the list: render nothing)
  const [view, setView] = useState({ status: 'loading' });
  const [links, setLinks] = useState([]);
  // candidates: loading | ready | unavailable (404, route not live on this server) | failed
  const [candidates, setCandidates] = useState({ status: 'loading', all: [] });
  const [choice, setChoice] = useState('');
  const [confirmingId, setConfirmingId] = useState(null);
  const [message, setMessage] = useState('');
  const [pending, setPending] = useState(false);
  const [stepUp, setStepUp] = useState(null);

  const aliveRef = useRef(false);
  const onChangedRef = useRef(onLinksChanged);
  onChangedRef.current = onLinksChanged;
  const stepUpResolveRef = useRef(null);

  // Opens the step-up dialog for a 401 challenge and resolves true when the user completes it, false when cancelled.
  const askStepUp = (challenge) => new Promise((resolve) => {
    stepUpResolveRef.current = resolve;
    setStepUp(challenge);
  });
  const settleStepUp = (completed) => {
    setStepUp(null);
    stepUpResolveRef.current?.(completed);
    stepUpResolveRef.current = null;
  };

  // Core's context route; one retry after a completed step-up. A second refusal propagates to the caller.
  const fetchContext = async () => {
    try {
      return await roomServiceLinksApi.getServiceLinksContext();
    } catch (cause) {
      const challenge = getStepUpChallengeFromError(cause);
      if (!challenge) throw cause;
      const completed = await askStepUp(challenge);
      if (!completed) throw cause;
      return roomServiceLinksApi.getServiceLinksContext();
    }
  };

  // Owner-only picker: the room organisation's active Mycelium installations, from Core's context route.
  const ownerFallback = async () => {
    try {
      const context = await fetchContext();
      const installations = Array.isArray(context?.installations) ? context.installations : [];
      const all = installations
        .filter((i) => i.appCode === MYCELIUM && i.status === 'active'
          && organisationId != null && String(i.ownerOrgId) === String(organisationId))
        .map((i) => ({
          installation_uuid: i.installationUuid,
          app_code: MYCELIUM,
          install_label: i.installLabel || i.hostname || '',
        }));
      return { status: 'ready', all };
    } catch (cause) {
      return { status: statusOf(cause) === 403 ? 'forbidden' : 'failed', all: [] };
    }
  };

  const load = useCallback(async () => {
    let listed;
    try {
      listed = await roomServiceLinksApi.list(roomId);
    } catch (cause) {
      if (!aliveRef.current) return;
      setView({ status: statusOf(cause) === 403 ? 'hidden' : 'failed' });
      return;
    }
    let all = [];
    let candidateStatus = 'ready';
    try {
      // candidates is live: it is used and the owner-only context fallback is never called.
      const found = await roomServiceLinksApi.candidates(roomId);
      all = Array.isArray(found?.candidates) ? found.candidates : [];
    } catch (cause) {
      if (statusOf(cause) === 404) {
        const fallback = await ownerFallback();
        candidateStatus = fallback.status;
        all = fallback.all;
      } else {
        candidateStatus = 'failed';
      }
    }
    if (!aliveRef.current) return;
    setLinks(Array.isArray(listed?.links) ? listed.links : []);
    setView({ status: 'ready' });
    // Only Mycelium candidates are offered. The full list is kept to name an active link.
    setCandidates({
      status: candidateStatus,
      all,
      options: all.filter((c) => c.app_code === MYCELIUM),
    });
  }, [roomId, organisationId]);

  useEffect(() => {
    aliveRef.current = true;
    load();
    return () => { aliveRef.current = false; };
  }, [load]);

  const afterChange = async () => {
    await load();
    if (aliveRef.current) onChangedRef.current?.();
  };

  const link = async () => {
    setPending(true);
    setMessage('');
    try {
      await roomServiceLinksApi.link(roomId, choice);
      if (!aliveRef.current) return;
      setChoice('');
      setPending(false);
      await afterChange();
    } catch (cause) {
      if (!aliveRef.current) return;
      setMessage(linkRefusalText(cause));
      setPending(false);
      await load();
    }
  };

  const unlink = async (linkId) => {
    setPending(true);
    setMessage('');
    setConfirmingId(null);
    try {
      await roomServiceLinksApi.unlink(roomId, linkId);
      if (!aliveRef.current) return;
      setPending(false);
      await afterChange();
    } catch (cause) {
      if (!aliveRef.current) return;
      setMessage(linkRefusalText(cause));
      setPending(false);
      await load();
    }
  };

  if (view.status === 'hidden') return null;

  const active = links.filter((l) => l.status === 'active');
  const labelFor = (uuid) => {
    const match = candidates.all.find((c) => c.installation_uuid === uuid);
    return match?.install_label || `Node ${shortId(uuid)}`;
  };
  const linkedLine = (l) => `Linked by user ${l.linked_by_user_id} on ${dateText(l.created_at)}`;

  const unlinkControl = (l) => (
    confirmingId === l.id ? (
      <span className="rc-confirm" role="group" aria-label="Confirm unlink">
        <span>Unlink this node from the room?</span>{' '}
        <button type="button" className="btn btn-outline" disabled={pending} data-testid={`room-linked-node-confirm-${l.id}`} onClick={() => unlink(l.id)}>
          Confirm unlink
        </button>{' '}
        <button type="button" className="btn btn-outline" onClick={() => setConfirmingId(null)}>Cancel</button>
      </span>
    ) : (
      <button type="button" className="btn btn-outline" disabled={pending} data-testid={`room-linked-node-unlink-${l.id}`} onClick={() => setConfirmingId(l.id)}>
        Unlink
      </button>
    )
  );

  const linkBody = () => {
    if (candidates.status === 'loading') return <p className="rc-muted">Loading nodes…</p>;
    if (candidates.status === 'forbidden') return <p className="rc-muted">{OWNER_ONLY}</p>;
    if (candidates.status === 'failed') return <p className="rc-muted">{LIST_FAILED}</p>;
    if (candidates.options.length === 0) return <p className="rc-muted">{NO_NODE}</p>;
    return (
      <div className="rc-link-choose">
        <label>
          Node{' '}
          <select value={choice} disabled={pending} data-testid="room-linked-node-select" onChange={(e) => setChoice(e.target.value)}>
            <option value="">Choose a node</option>
            {candidates.options.map((c) => (
              <option key={c.installation_uuid} value={c.installation_uuid}>
                {c.install_label || shortId(c.installation_uuid)}
              </option>
            ))}
          </select>
        </label>{' '}
        <button type="button" className="btn btn-primary" disabled={!choice || pending} data-testid="room-linked-node-link" onClick={link}>
          Link
        </button>
      </div>
    );
  };

  let body = null;
  if (view.status === 'loading') body = <p className="rc-muted">Loading…</p>;
  else if (view.status === 'failed') body = <p className="rc-muted" role="alert">{LOAD_FAILED}</p>;
  else if (active.length === 0) body = linkBody();
  else if (active.length === 1) {
    const [only] = active;
    body = (
      <p data-testid="room-linked-node-current">
        Linked to <strong>{labelFor(only.installation_uuid)}</strong>. {linkedLine(only)}. {unlinkControl(only)}
      </p>
    );
  } else {
    body = (
      <>
        <p role="alert">{AMBIGUOUS}</p>
        <ul data-testid="room-linked-node-ambiguous">
          {active.map((l) => (
            <li key={l.id}>
              <strong>{labelFor(l.installation_uuid)}</strong>. {linkedLine(l)}. {unlinkControl(l)}
            </li>
          ))}
        </ul>
      </>
    );
  }

  return (
    <section className="rc-section" data-testid="room-linked-node">
      <h4>Linked Mycelium node</h4>
      {body}
      {message && <p role="alert">{message}</p>}
      <StepUpDialog
        challenge={stepUp}
        title="Verify to list this room's nodes"
        description="Confirm your identity before StudioAEye lists the Mycelium nodes you can link to this room."
        onComplete={() => settleStepUp(true)}
        onCancel={() => settleStepUp(false)}
      />
    </section>
  );
};

export default RoomLinkedNodePanel;
